import React from 'react';
import { Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import './StructuredMessageForm.scss';
import {
  SalvButton,
  SalvFieldHeader,
  SalvIconButton,
  SalvTextArea,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import {
  getDefaultValue,
  getField,
  getMessengerFieldConfiguration,
  getSchemaType,
  MessengerTemplateFieldShowWhenConfiguration,
  setRequired,
  testInput,
} from './helper';
import {
  BridgeApiConversation,
  BridgeApiTemplate,
  BridgeApiTemplateField,
  BridgeApiTemplateFieldTypeEnum,
  InternalApiAlertRfiMessage,
} from '../api-internal';
import { JsonMessage, JsonMessageTypeEnum } from './Message';
import { alertRfiMessageApi, attachmentApi } from '../apis';
import { faPaperPlane, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';

import * as Yup from 'yup';
import { AnySchema } from 'yup';
import { sorted } from './common/helper';

function constructValidationSchema(fields: BridgeApiTemplateField[], requestedAttributes: string[]) {
  return Yup.object().shape(
    {
      requestedFields: Yup.object().shape({
        ...fields
          .filter((templateField) => requestedAttributes.includes(templateField.name))
          .reduce((obj, templateField) => {
            const fieldConf = getMessengerFieldConfiguration(templateField);
            const showWhenConf = fieldConf.show_when;
            let fieldSchema = getSchemaType(templateField);

            if (showWhenConf) {
              fieldSchema = fieldSchema.when(showWhenConf.field_id, {
                is: showWhenConf.field_value,
                then: (schema: AnySchema) => setRequired(schema, templateField),
              });
            } else {
              fieldSchema = setRequired(fieldSchema, templateField);
            }

            return { ...obj, [templateField.name]: fieldSchema };
          }, {}),
      }),
      responseComment: Yup.string().test('Cannot contain HTML', 'Cannot contain HTML', (value) => testInput(value)),
    },
    []
  );
}
function displayRequestedField(
  field: BridgeApiTemplateField,
  values: StructuredMessageFormValues,
  setFieldValue: any,
  errors: FormikErrors<StructuredMessageFormValues>,
  dependentFields: { [parentName: string]: BridgeApiTemplateField[] }
) {
  const showWhenConf = getMessengerFieldConfiguration(field).show_when;

  const isVisible = (showWhenConf: MessengerTemplateFieldShowWhenConfiguration) => {
    if (Array.isArray(values.requestedFields[showWhenConf.field_id])) {
      return (values.requestedFields[showWhenConf.field_id] as any[]).some(
        (option: string) => option === showWhenConf.field_value
      );
    }

    return values.requestedFields[showWhenConf.field_id] === showWhenConf.field_value;
  };

  if (showWhenConf && !isVisible(showWhenConf)) {
    return;
  }

  const fieldRow = (
    <div key={field.name} className='requested-field-row m-b-16'>
      <SalvFieldHeader
        required={field.required}
        size={Size.MEDIUM}
        label={
          field.description ? (
            <div className='description-tooltip'>
              <SalvTooltipWrapper tooltip={field.description} delay={0}>
                <SalvIconButton
                  icon={faCircleInfo}
                  className='text-muted m-r-8 cursor-pointer'
                  ariaLabel='description-icon'
                />
              </SalvTooltipWrapper>
              {field.displayName}
            </div>
          ) : (
            field.displayName
          )
        }
        className='m-r-16'
      />
      <div className='field-container'>
        {getField(
          field,
          values.requestedFields[field.name],
          errors.requestedFields?.[field.name] !== undefined,
          (value: string) => setFieldValue(`requestedFields.${field.name}`, value),
          Size.MEDIUM
        )}
      </div>
    </div>
  );

  if (
    (field.type === BridgeApiTemplateFieldTypeEnum.BOOLEAN || field.type === BridgeApiTemplateFieldTypeEnum.SELECT) &&
    dependentFields[field.name]
  ) {
    return (
      <div className='dependent-block p-t-16 m-b-16' key={field.name + '_wrapper'}>
        {fieldRow}
        {sorted(dependentFields[field.name]).map((dependentField) => {
          return displayRequestedField(dependentField, values, setFieldValue, errors, dependentFields);
        })}
      </div>
    );
  } else {
    return fieldRow;
  }
}

export interface StructuredMessageFormValues {
  responseComment?: string;
  requestedFields: {
    [key: string]: boolean | string | number | File[];
  };
}

async function getMessageAttributes(
  values: StructuredMessageFormValues,
  requestedFields: BridgeApiTemplateField[],
  conversation: BridgeApiConversation
) {
  const attachmentIds: string[] = [];

  const attributes = await Object.entries(values.requestedFields)
    .filter(([key]) => {
      const field = requestedFields.find((field) => field.name === key);

      if (!field) {
        return false;
      }

      const showWhenConf = getMessengerFieldConfiguration(field).show_when;

      if (showWhenConf) {
        return values.requestedFields[showWhenConf.field_id] === showWhenConf.field_value;
      } else {
        return true;
      }
    })
    .reduce(async (acc, [key, val]) => {
      const field = requestedFields.find((field) => field.name === key);

      if (!field || val === undefined) {
        return await acc;
      }

      if (field.type !== BridgeApiTemplateFieldTypeEnum.FILE) {
        return { ...(await acc), [key]: val };
      } else {
        const fileFieldArrayValue = await Promise.all(
          Object.values(val)
            .filter((fileValue) => fileValue instanceof File)
            .map(async (attachment: File) => {
              const attachmentId = (
                await attachmentApi.uploadMessageAttachment({
                  file: attachment,
                  conversationId: conversation.id,
                })
              ).handle;
              attachmentIds.push(attachmentId);
              return {
                fileName: attachment.name,
                id: attachmentId,
              };
            })
        );

        if (fileFieldArrayValue !== undefined) {
          return {
            ...(await acc),
            [field.name]: fileFieldArrayValue,
          };
        } else {
          return await acc;
        }
      }
    }, Promise.resolve({}));

  return { attributes: attributes, attachmentIds: attachmentIds };
}
const StructuredMessageForm = ({
  alertRfiId,
  conversation,
  template,
  onSubmit,
  isPreview,
}: {
  alertRfiId: string;
  conversation: BridgeApiConversation;
  template: BridgeApiTemplate;
  onSubmit: (message: InternalApiAlertRfiMessage) => void;
  isPreview?: boolean;
}): React.JSX.Element => {
  const dependentFields = template.requestedFields
    .filter((field) => getMessengerFieldConfiguration(field).show_when !== undefined)
    .reduce(function (rv: { [p: string]: BridgeApiTemplateField[] }, x) {
      const showWhenConf = getMessengerFieldConfiguration(x).show_when!;
      (rv[showWhenConf.field_id] = rv[showWhenConf.field_id] || []).push(x);
      return rv;
    }, {});

  // requested template fields and dependent fields + custom attributes
  const requestedFieldNames = [
    ...new Set(
      conversation.requestedFields
        ? conversation.requestedFields.fromTemplate
            .concat(
              conversation.requestedFields.fromTemplate
                .flatMap((field) => dependentFields[field])
                .filter((field) => field)
                .map((field) => field.name) ?? []
            )
            .concat(conversation.requestedFields.custom.map((it) => it.name))
        : []
    ),
  ];

  // template fields + custom fields
  const requestedFields = template.requestedFields.concat(conversation.requestedFields?.custom ?? []);

  const sendMessage = async (
    values: StructuredMessageFormValues,
    { setSubmitting }: FormikHelpers<StructuredMessageFormValues>
  ) => {
    if (isPreview) {
      return;
    }

    setSubmitting(true);
    try {
      const { attributes, attachmentIds } = await getMessageAttributes(values, requestedFields, conversation);

      const structureMessageJson: JsonMessage = {
        type: JsonMessageTypeEnum.STRUCTURED_REQUEST_RESPONSE,
        message: `Hi,\n\nHere are the details you requested:`,
        attributes: attributes,
        responseComment: values.responseComment,
      };
      const message = await alertRfiMessageApi.addMessage({
        alertRfiId: alertRfiId,
        internalApiCreateAlertRfiMessage: {
          content: JSON.stringify(structureMessageJson),
          attachmentIds,
        },
      });
      onSubmit(message);
    } catch (e: any) {
      console.warn('something went wrong when sending a message', e);
    } finally {
      setSubmitting(false);
    }
  };

  const parentRequestedFields = requestedFields.filter((field) => {
    const showWhenConf = getMessengerFieldConfiguration(field).show_when;
    return requestedFieldNames.includes(field.name) && showWhenConf === undefined;
  });

  const initialValues: StructuredMessageFormValues = {
    requestedFields: requestedFields
      .filter((templateField) => requestedFieldNames.includes(templateField.name))
      .reduce((obj, templateField: BridgeApiTemplateField) => {
        return Object.assign(obj, {
          [templateField.name]: getDefaultValue(templateField),
        });
      }, {}),
    ...(template.responseComment ? { responseComment: '' } : {}),
  };

  const showSend = parentRequestedFields.length !== 0 && !isPreview;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={sendMessage}
      validationSchema={constructValidationSchema(requestedFields, requestedFieldNames)}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ values, setFieldValue, isSubmitting, errors }: FormikProps<StructuredMessageFormValues>) => {
        return (
          <Form>
            <div className='structured-message-form p-a-16 m-t-12'>
              {sorted(parentRequestedFields).map((field: BridgeApiTemplateField) =>
                displayRequestedField(field, values, setFieldValue, errors, dependentFields)
              )}
              {template.responseComment && (
                <SalvTextArea
                  className={showSend ? 'm-b-16' : ''}
                  onChange={(value: string) => {
                    setFieldValue('responseComment', value);
                  }}
                  value={values.responseComment}
                  isInvalid={!!errors.responseComment}
                  placeholder='Specify your answer if needed'
                  ariaLabel='Response comment'
                  maxRows={10}
                  autoSizing={true}
                />
              )}
              {showSend && (
                <div className='d-flex justify-content-end'>
                  <SalvButton
                    type='submit'
                    variant='primary'
                    disabled={isSubmitting}
                    size={Size.MEDIUM}
                    icon={faPaperPlane}
                    label='Send'
                  />
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default StructuredMessageForm;
